<script lang="ts">
  import Icon from '../components/Icon.svelte';
  import { MATERIAL_TYPE_ICONS } from '../lib/icons';
  import { onMount } from 'svelte';
  import { replace } from 'svelte-spa-router';
  import TopBar from '../components/TopBar.svelte';
  import { applySession, session } from '../lib/session';
  import { adminMode } from '../lib/admin-mode';
  import { api, ApiError } from '../lib/api';
  import { getInitData, isInsideTelegram } from '../lib/telegram';
  import { durationLabel } from '../lib/format';
  import { renderMarkdown } from '../lib/markdown';
  import HeartButton from '../components/HeartButton.svelte';
  import UpsellBlock from '../components/UpsellBlock.svelte';
  import type { CategoryDetailDTO, MaterialSummaryDTO, MaterialType } from '../lib/types';

  // svelte-spa-router passes matched route params as a prop — more reliable
  // than the params store in Svelte 5 runes mode.
  interface Props {
    params?: { id?: string };
  }
  let { params }: Props = $props();
  const categoryId = $derived(params?.id ?? '');

  let category = $state<CategoryDetailDTO | null>(null);
  let notFound = $state(false);
  let error = $state<string | null>(null);
  let filter = $state<MaterialType | 'all'>('all');

  // Admin acting in editor mode — sees the edit FAB and skips user-facing
  // affordances like progress / favorite. In user mode admin behaves like
  // a regular subscriber for browsing.
  const adminEditing = $derived(($session?.isAdmin ?? false) && $adminMode === 'admin');

  // Locked group: catalog stays visible, but content is gated. Backend already
  // resolves hasAccess (admins/Club/owners → true), so this just drives the UI.
  const locked = $derived(!!category && !category.hasAccess);
  // With per-lesson access a category can be partially open. Suppress the
  // whole-section upsell when the user can already open at least one lesson.
  const hasAnyLessonAccess = $derived(
    !!category && category.materials.some((m) => m.hasAccess),
  );

  const TYPE_LABELS: Record<MaterialType, string> = {
    video: 'Відео',
    audio: 'Аудіо',
    text: 'Текст',
  };

  onMount(async () => {
    // Refresh session in background (non-blocking)
    (async () => {
      try {
        if (isInsideTelegram()) applySession(await api.authMe(getInitData()));
        else applySession(await api.authSession());
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          replace(import.meta.env.DEV ? '/' : '/login');
        }
      }
    })();

    if (!categoryId) {
      notFound = true;
      return;
    }

    try {
      category = await api.category(categoryId);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 400)) {
        notFound = true;
      } else {
        error = 'Не вдалось завантажити розділ';
      }
    }
  });

  // Per-type counts for the filter pills
  const counts = $derived.by(() => {
    const c: Record<MaterialType | 'all', number> = {
      all: category?.materials.length ?? 0,
      video: 0,
      audio: 0,
      text: 0,
    };
    for (const m of category?.materials ?? []) c[m.type]++;
    return c;
  });

  const visibleMaterials = $derived<MaterialSummaryDTO[]>(
    !category
      ? []
      : filter === 'all'
        ? category.materials
        : category.materials.filter((m) => m.type === filter),
  );

  /** Optimistic toggle — flip locally, call API, revert on error. */
  async function toggleFavorite(mat: MaterialSummaryDTO): Promise<void> {
    if (!category) return;
    const before = mat.isFavorite;
    mat.isFavorite = !before;
    category = { ...category }; // trigger reactivity
    try {
      const { isFavorite } = await api.toggleFavorite(mat.id);
      mat.isFavorite = isFavorite;
      category = { ...category };
    } catch {
      mat.isFavorite = before;
      category = { ...category };
    }
  }
</script>

<div class="screen">
  <TopBar
    title={category?.name ?? 'Розділ'}
    backHref="/catalog"
    showLogo={false}
  />
  <div class="scroll">
    {#if notFound}
      <div class="empty">Розділ не знайдено</div>
    {:else if error}
      <div class="empty">{error}</div>
    {:else if category}
      <div class="card-list">
        {#if category.description}
          <div class="promo">
            <div class="promo-text md-content">{@html renderMarkdown(category.description)}</div>
          </div>
        {/if}

        {#if locked && !hasAnyLessonAccess}
          <UpsellBlock
            title="Цей розділ поки закритий"
            text="Для доступу до цього розділу активуйте підписку."
          />
        {/if}

        <div class="filters">
          {#each [['all', 'Всі'], ['video', TYPE_LABELS.video], ['audio', TYPE_LABELS.audio], ['text', TYPE_LABELS.text]] as [key, label]}
            {@const k = key as MaterialType | 'all'}
            <button
              class="filter"
              class:active={filter === k}
              onclick={() => (filter = k)}
              disabled={k !== 'all' && counts[k] === 0}
            >
              {label} <span class="filter-count">{counts[k]}</span>
            </button>
          {/each}
        </div>

        {#if visibleMaterials.length === 0}
          <div class="empty empty-soft">
            {filter === 'all' ? 'Скоро тут будуть матеріали' : 'Немає матеріалів цього типу'}
          </div>
        {:else}
          <div class="materials card-list">
            {#each visibleMaterials as m (m.id)}
              {@const mLocked = !m.hasAccess}
              <a class="material" class:locked={mLocked} href={`#/material/${m.id}`}>
                <div class="material-icon"><Icon name={MATERIAL_TYPE_ICONS[m.type]} /></div>
                <div class="material-text">
                  <div class="material-title">{m.title}</div>
                  {#if m.durationSec}
                    <div class="material-meta">{durationLabel(m.durationSec)}</div>
                  {/if}
                  {#if !adminEditing && !mLocked}
                    {#if m.completedAt}
                      <div class="material-progress-row done"><Icon name="check" size="xs" /> Переглянуто</div>
                    {:else if m.maxProgressPct > 0}
                      <div class="material-progress-row">
                        <span class="material-pct">{m.maxProgressPct}%</span>
                        <div class="material-progress">
                          <div class="material-progress-bar" style="width: {m.maxProgressPct}%"></div>
                        </div>
                      </div>
                    {/if}
                  {/if}
                </div>
                {#if mLocked}
                  <div class="material-lock" aria-label="Заблоковано"><Icon name="lock" label="Заблоковано" /></div>
                {:else}
                  {#if !adminEditing}
                    <HeartButton isFavorite={m.isFavorite} onToggle={() => toggleFavorite(m)} size={20} />
                  {/if}
                  <div class="material-chevron">›</div>
                {/if}
              </a>
            {/each}
          </div>
        {/if}
      </div>
    {/if}
  </div>

  {#if adminEditing && category}
    <a class="admin-edit-fab" href={`#/admin/category/${category.id}/edit`} aria-label="Редагувати">
      <span class="edit-icon"><Icon name="square-pen" /></span> Редагувати
    </a>
  {/if}
</div>

<style>
  .admin-edit-fab {
    position: fixed;
    right: 16px;
    bottom: calc(76px + env(safe-area-inset-bottom));
    background: linear-gradient(135deg, var(--dark3), var(--pu3));
    color: var(--text-light);
    padding: 11px 16px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
    z-index: 50;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .edit-icon {
    display: inline-block;
    transform: scaleX(-1);
  }
  .screen {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--bg);
  }
  .scroll {
    flex: 1;
    overflow-y: auto;
    padding: 16px 16px 100px;
  }
  .scroll::-webkit-scrollbar { width: 3px; }
  .scroll::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 2px; }

  .promo {
    padding: 14px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    margin-bottom: 16px;
  }
  .promo-text {
    font-size: 13px;
    line-height: 1.55;
    color: var(--text);
  }

  .filters {
    display: flex;
    gap: 6px;
    overflow-x: auto;
    margin-bottom: 14px;
    padding-bottom: 4px;
    scrollbar-width: none;
  }
  .filters::-webkit-scrollbar { display: none; }
  .filter {
    flex-shrink: 0;
    padding: 7px 12px;
    border-radius: 999px;
    border: 1px solid var(--border2);
    background: transparent;
    font-size: 12px;
    font-weight: 500;
    color: var(--text-muted);
    cursor: pointer;
    white-space: nowrap;
  }
  .filter.active {
    background: var(--pu);
    border-color: var(--pu);
    color: var(--text-light);
  }
  .filter:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .filter-count {
    opacity: 0.7;
    margin-left: 2px;
  }

  .material {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    margin-bottom: 8px;
    text-decoration: none;
    color: inherit;
  }
  .material-chevron {
    color: var(--text-muted);
    font-size: 20px;
    line-height: 1;
    flex-shrink: 0;
  }
  .material.locked {
    opacity: 0.6;
  }
  .material.locked .material-icon {
    filter: grayscale(0.6);
  }
  .material-lock {
    font-size: 14px;
    line-height: 1;
    flex-shrink: 0;
  }
  .material-icon {
    font-size: 20px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg);
    border-radius: 8px;
    flex-shrink: 0;
  }
  .material-text {
    flex: 1;
    min-width: 0;
  }
  .material-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .material-meta {
    font-size: 11px;
    color: var(--text-muted);
    margin-top: 2px;
  }
  .material-progress-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 6px;
    font-size: 11px;
    font-weight: 600;
  }
  .material-progress-row.done {
    color: var(--status-active);
  }
  .material-pct {
    color: var(--accent-text);
    flex-shrink: 0;
  }
  .material-progress {
    flex: 1;
    max-width: 50%;
    height: 2px;
    background: var(--border);
    border-radius: 999px;
    overflow: hidden;
  }
  .material-progress-bar {
    height: 100%;
    background: var(--pu);
    transition: width 0.3s var(--ease);
  }

  .empty {
    text-align: center;
    padding: 60px 20px;
    color: var(--text-muted);
    font-size: 13px;
  }
  .empty-soft {
    padding: 32px 20px;
    border: 1px dashed var(--border2);
    border-radius: 12px;
    background: var(--bg2);
  }
</style>
